import { FlowFormPanel } from "#batteries-included-components/Panels/FormPanels/FlowFormPanel/FlowFormPanel";
import { useGetOneFlow, useUpdateFlow } from "#hooks/adapters/useFlows";
import { useNavigate, useParams } from "#routers/hooks";
import { FlowEditRoute } from "#routes/organization/flows/[flowId]/detail/edit/index";
import useLocalization from "#src/hooks/useLocalization";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useBreadcrumbsFromRoute } from "#utils/route";
import { Page } from "@validereinc/common-components";
import { AssetType, type FlowUpdateOneRequestType } from "@validereinc/domain";
import React from "react";
import { FlowDetailRoute } from "../index";
import { useStorageKey } from "#src/hooks/useStorageKey";

const EditFlowPageContent = ({ flowId }: { flowId: string }) => {
  const { stepper, footer } = useMultiStepFormContext();
  const { localize, isLoading: isLocalizationLoading } = useLocalization();
  const { data: flowData, isLoading: isFlowLoading } = useGetOneFlow(
    {
      id: flowId,
    },
    {
      enabled: !!flowId,
    }
  );
  const flow = flowData?.data;

  const [breadcrumbs] = useBreadcrumbsFromRoute(FlowEditRoute, {
    detail: { title: flow?.name },
  });

  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["asset-builder", localize(AssetType.FLOW)]
  );

  const isPageLoading = isLocalizationLoading || isFlowLoading;

  return (
    <Page
      breadcrumbs={breadcrumbs}
      title={localize(FlowEditRoute.title)}
      actionRow={stepper}
      footer={footer}
      isLoading={isPageLoading}
    >
      <FlowFormPanel
        flowId={flowId}
        isConnectionEditOnly
        filterConfigStorageKey={filterConfigStorageKey}
      />
    </Page>
  );
};

export const EditFlowPage = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const backToFlow = () =>
    navigate(FlowDetailRoute.toLinkParts({ pathParams: { flowId } }));

  const updateFlow = useUpdateFlow({
    onSuccess: backToFlow,
  });

  const onSubmit = (values: Array<FlowUpdateOneRequestType["data"]>) => {
    updateFlow.mutate({
      id: flowId,
      data: values.reduce((total, current) => ({ ...total, ...current }), {}),
    });
  };

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Connections",
        },
      ]}
      onCancel={backToFlow}
      onSubmit={onSubmit}
      onSubmitMutationState={{
        isSubmitting: updateFlow.isLoading,
      }}
    >
      <EditFlowPageContent flowId={flowId} />
    </MultiStepFormProvider>
  );
};
